import React, { useState } from "react";
import { apiService } from "../services/api";

interface EmailVerificationProps {
  email: string;
  onVerificationSuccess: () => void;
  onCancel: () => void;
}

export const EmailVerification: React.FC<EmailVerificationProps> = ({
  email,
  onVerificationSuccess,
  onCancel,
}) => {
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();

    if (verificationCode.length !== 6) {
      setError("Please enter a 6-digit verification code");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await apiService.verifyEmail(email, verificationCode);
      onVerificationSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    setError(null);
    setResendSuccess(false);

    try {
      await apiService.resendEmailVerification(email);
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to resend code");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: "400px", margin: "50px auto", padding: "20px" }}>
      <h2>Email Verification</h2>

      <div
        style={{
          backgroundColor: "#e7f3ff",
          border: "1px solid #b3d9ff",
          borderRadius: "4px",
          padding: "16px",
          marginBottom: "24px",
        }}
      >
        <p style={{ margin: "0 0 8px 0", fontWeight: "500" }}>
          Verification Required
        </p>
        <p style={{ margin: 0, fontSize: "14px", color: "#004085" }}>
          A 6-digit verification code has been sent to:
        </p>
        <p
          style={{
            margin: "8px 0 0 0",
            fontSize: "14px",
            fontWeight: "bold",
            color: "#004085",
          }}
        >
          {email}
        </p>
      </div>

      <form onSubmit={handleVerify}>
        <div style={{ marginBottom: "20px" }}>
          <label
            htmlFor="verificationCode"
            style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}
          >
            Verification Code:
          </label>
          <input
            type="text"
            id="verificationCode"
            value={verificationCode}
            onChange={(e) =>
              setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            placeholder="000000"
            maxLength={6}
            required
            autoFocus
            style={{
              width: "100%",
              padding: "12px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "18px",
              letterSpacing: "0.5em",
              textAlign: "center",
              fontFamily: "monospace",
            }}
          />
          <div style={{ marginTop: "8px", fontSize: "12px", color: "#6c757d" }}>
            Enter the 6-digit code from your email
          </div>
        </div>

        {error && (
          <div
            style={{
              color: "#dc3545",
              backgroundColor: "#f8d7da",
              border: "1px solid #f5c6cb",
              borderRadius: "4px",
              padding: "10px",
              marginBottom: "15px",
              fontSize: "14px",
            }}
          >
            {error}
          </div>
        )}

        {resendSuccess && (
          <div
            style={{
              color: "#155724",
              backgroundColor: "#d4edda",
              border: "1px solid #c3e6cb",
              borderRadius: "4px",
              padding: "10px",
              marginBottom: "15px",
              fontSize: "14px",
            }}
          >
            Verification code resent successfully!
          </div>
        )}

        <button
          type="submit"
          disabled={loading || verificationCode.length !== 6}
          style={{
            width: "100%",
            padding: "12px",
            backgroundColor:
              verificationCode.length === 6 && !loading ? "#28a745" : "#6c757d",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor:
              verificationCode.length === 6 && !loading
                ? "pointer"
                : "not-allowed",
            fontSize: "16px",
            fontWeight: "500",
            marginBottom: "12px",
          }}
        >
          {loading ? "Verifying..." : "Verify Email"}
        </button>

        <div style={{ display: "flex", gap: "8px" }}>
          <button
            type="button"
            onClick={handleResendCode}
            disabled={resendLoading}
            style={{
              flex: 1,
              padding: "10px",
              backgroundColor: "transparent",
              color: "#007bff",
              border: "1px solid #007bff",
              borderRadius: "4px",
              cursor: resendLoading ? "not-allowed" : "pointer",
              fontSize: "14px",
            }}
          >
            {resendLoading ? "Sending..." : "Resend Code"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            style={{
              flex: 1,
              padding: "10px",
              backgroundColor: "transparent",
              color: "#6c757d",
              border: "1px solid #6c757d",
              borderRadius: "4px",
              cursor: loading ? "not-allowed" : "pointer",
              fontSize: "14px",
            }}
          >
            Cancel
          </button>
        </div>
      </form>

      <div
        style={{
          marginTop: "20px",
          padding: "12px",
          backgroundColor: "#fff3cd",
          border: "1px solid #ffc107",
          borderRadius: "4px",
          fontSize: "12px",
          color: "#856404",
        }}
      >
        <strong>Didn't receive the code?</strong>
        <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
          <li>Check your spam/junk folder</li>
          <li>Make sure the email address is correct</li>
          <li>Click "Resend Code" to try again</li>
          <li>Contact your administrator if you continue to have issues</li>
        </ul>
      </div>
    </div>
  );
};
